import idx from "idx"
import { CONSTANTS } from "@data"
import { logTool, twiceFieldCheck, joinNewLines } from "@jsUtils"

const { PAGE_NAMES } = CONSTANTS

const log = logTool(true)

const DEFAULT_HEADER_LEVEL = 1
const DEFAULT_SUBHEAD_LEVEL = 4

// Some fields on some pages come from ... different fields
// got it? good. yeah, i know.
const REMAPPED_FIELDS = {
  [PAGE_NAMES.TECH_SPECS]: {
    inlineHeader: "inline"
  },
  // example: on the our_people page,
  // the eyebrow comes from title, not eyebrow
  [PAGE_NAMES.CONTACT]: {
    eyebrow: "title"
  },
  [PAGE_NAMES.OUR_PEOPLE]: {
    eyebrow: "title"
  },
  // on the legal pages, the headline is from
  // the title
  [PAGE_NAMES.LEGAL]: {
    headline: "title"
  }
}

const getByFieldName = (classes, fieldName) => idx(classes, _ => _[fieldName])

/**
 * Remap some fields for some pages due to an inconsistent data model
 * @param {object} data - data object
 * @param {string} field - string field key
 * @param {string} pageName - name of page to check for remapping
 * @return string
 */
const fieldByPageName = (data, field, pageName) => {
  const pages = REMAPPED_FIELDS
  let fieldName
  if (pageName in pages && field in pages[pageName]) {
    // log("01 field:", field, "pageName:", pageName)
    fieldName = pages[pageName][field]
  } else {
    // log("02")
    fieldName = field
  }
  // log("fieldname:", fieldName, "in", data)
  // return idx(data, _ => _[fieldName])
  return twiceFieldCheck(data, fieldName)
}

/**
 *
 * @param {object} contentData - contentful data
 * @param {object} classes - classes prop map from usage
 * @param {object} levels - levels prop from usage
 * @param {string} page - page prop name from usage
 * @returns {object}
 */
const headerTextFactory = (contentData, classes, levels, page) => {
  const headerTextRaw = fieldByPageName(contentData, "headline", page)
  const headerText = {
    text: joinNewLines(headerTextRaw),
    level: getByFieldName(levels, "headerText") || DEFAULT_HEADER_LEVEL,
    className: getByFieldName(classes, "headerText")
  }
  return headerText
}

/**
 *
 * @param {object} contentData - contentful data
 * @param {object} classes - classes prop map from usage
 * @param {string} page - page prop name from usage
 * @returns {object}
 */
const eyebrowFactory = (contentData, classes, page) => {
  const eyebrowRaw = fieldByPageName(contentData, "eyebrow", page)
  const eyebrow = eyebrowRaw
    ? {
        text: eyebrowRaw,
        className: getByFieldName(classes, "eyebrow")
      }
    : false
  return eyebrow
}

const inlineHeaderFactory = (contentData, classes, page) => {
  const inlineHeaderRaw = fieldByPageName(contentData, "inlineHeader", page)
  const inlineHeader = inlineHeaderRaw
    ? {
        text: inlineHeaderRaw,
        className: getByFieldName(classes, "inlineHeader")
      }
    : false
  return inlineHeader
}

/**
 *
 * @param {object} contentData - contentful data
 * @param {object} classes - classes prop map from usage
 * @param {object} levels - levels prop from usage
 * @returns {object}
 */
const subheadFactory = (contentData, classes, levels) => {
  const subheadRaw = twiceFieldCheck(contentData, "subhead")
  // description is only used on the home page
  const description = idx(contentData, _ => _.description)
  // log("subheadFactory work:", subheadRaw, description)
  const subhead =
    subheadRaw || description
      ? {
          text: subheadRaw || description,
          level: getByFieldName(levels, "subhead") || DEFAULT_SUBHEAD_LEVEL,
          className: getByFieldName(classes, "subhead")
        }
      : false
  return subhead
}

/**
 *
 * @param {object} contentData - contentful data
 * @param {object} classes - classes prop map from usage
 * @returns {object}
 */
const copyFactory = (contentData, classes) => {
  const copyRaw = twiceFieldCheck(contentData, "headerCopy")
  const copy = copyRaw
    ? {
        text: copyRaw,
        className: getByFieldName(classes, "copy")
      }
    : false
  return copy
}

/**
 *
 * @param {object} contentData - contentful data
 * @param {object} classes - classes prop map from usage
 * @returns {object}
 */
const ctaFactory = (contentData, classes) => {
  const ctaLabel = idx(contentData, _ => _.ctaLabel)
  const ctaLink = idx(contentData, _ => _.ctaLink)
  const cta = ctaLabel
    ? {
        link: ctaLink,
        text: ctaLabel,
        className: getByFieldName(classes, "cta")
      }
    : false
  return cta
}

/**
 *
 * Transform content into correct format for the page in question, sourced from Contentful graphql
 * Our miscellaneous factories convert and normalize a mess of field names used in the page headers
 *
 * @param {object} contentData - typically page data, such as contentfulHome
 * @param {string} contentData.headline - main page headline
 * @param {string} contentData.description - page content in hero
 * @param {string} contentData.ctaLabel - link text
 * @param {string} contentData.ctaLink - link destination
 * @param {object} classes - object with misc classes
 * @param {string} classes.eyebrow - class for eyebrow
 * @param {string} classes.headerText - class for headerText
 * @param {string} classes.subhead - class for subhead
 * @param {string} classes.copy - class for copy
 * @param {string} classes.cta - class for cta
 * @param {string} classes.extraHeroSection - extra class for PageSection
 * @param {string} classes.extraHeroWrapper - extra class for div around Hero content
 * @param {string} classes.extraPageHeadingContent - extra class for row (old PageHeading extra class)
 * @param {string} classes.childWrapper - extra class for wrapping children
 *
 * @param {object} levels - object with misc heading levels identified
 * @param {number} levels.headerText - heading level for headerText
 * @param {number} levels.subhead - heading level for subhead
 * @param {string} page - page type the hero is on
 */
const transform = (contentData, classes, levels, page) => {
  // these fields are frequently remapped based on the page's source content
  const eyebrow = eyebrowFactory(contentData, classes, page)
  const headerText = headerTextFactory(contentData, classes, levels, page)
  const subhead = subheadFactory(contentData, classes, levels)
  const copy = copyFactory(contentData, classes)
  const cta = ctaFactory(contentData, classes)

  const miscClassNames = {
    extraHeroSection: getByFieldName(classes, "extraHeroSection"),
    extraHeroWrapperRow: getByFieldName(classes, "extraHeroWrapperRow"),
    childWrapper: getByFieldName(classes, "childWrapper"),
    extraPageHeadingContent: getByFieldName(classes, "extraPageHeadingContent")
  }

  // one off; tbd
  const inlineHeader = inlineHeaderFactory(contentData, classes, page)

  const images = idx(contentData, _ => _.heroImages) || idx(contentData, _ => _.headerImages)

  const content = {
    eyebrow,
    headerText,
    subhead,
    copy,
    cta,
    miscClassNames,
    images,
    inlineHeader
  }
  return {
    content
  }
}

export default {
  transform
}
